import React, { useState } from 'react';
import {
  Settings, User, Shield, Database, Bell, Globe, 
  Palette, Key, Users, Building2, CreditCard, FileText,
  Save, RefreshCw, Download, Upload, Eye, EyeOff, Lock, Plus
} from 'lucide-react';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'pharmacist' | 'technician' | 'manager';
  department: string;
  phone: string;
  avatar: string;
  lastLogin: string;
  status: 'active' | 'inactive' | 'suspended';
}

interface SystemConfig {
  id: string;
  category: 'general' | 'security' | 'notifications' | 'appearance' | 'integration';
  setting: string;
  value: string | boolean | number;
  type: 'text' | 'boolean' | 'number' | 'select';
  description: string;
  options?: string[];
}

interface SecurityLog {
  id: string;
  user: string;
  action: 'login' | 'logout' | 'password_change' | 'permission_change' | 'data_access';
  timestamp: string;
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'failed' | 'warning';
}

const mockUserProfile: UserProfile = {
  id: 'USR-001',
  name: 'Dr. Sarah Johnson',
  email: 'sarah.johnson@luniahealth.com',
  role: 'admin',
  department: 'Pharmacy Management',
  phone: '+1 (555) 123-4567',
  avatar: 'SJ',
  lastLogin: '2024-01-20 09:30 AM',
  status: 'active'
};

const mockSystemConfigs: SystemConfig[] = [
  {
    id: 'CONF-001',
    category: 'general',
    setting: 'Pharmacy Name',
    value: 'Lunia Health Pharmacy',
    type: 'text',
    description: 'Display name for the pharmacy system'
  },
  {
    id: 'CONF-002',
    category: 'general',
    setting: 'Time Zone',
    value: 'America/Puerto_Rico',
    type: 'select',
    description: 'System time zone for all date/time operations',
    options: ['America/Puerto_Rico', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles']
  },
  {
    id: 'CONF-003',
    category: 'security',
    setting: 'Two-Factor Authentication',
    value: true,
    type: 'boolean',
    description: 'Require 2FA for all user accounts'
  },
  {
    id: 'CONF-004',
    category: 'security',
    setting: 'Session Timeout',
    value: 30,
    type: 'number',
    description: 'User session timeout in minutes'
  },
  {
    id: 'CONF-005',
    category: 'notifications',
    setting: 'Email Notifications',
    value: true,
    type: 'boolean',
    description: 'Send email notifications for system events'
  },
  {
    id: 'CONF-006',
    category: 'notifications',
    setting: 'SMS Notifications',
    value: false,
    type: 'boolean',
    description: 'Send SMS notifications for critical alerts'
  },
  {
    id: 'CONF-007',
    category: 'appearance',
    setting: 'Theme',
    value: 'lunia',
    type: 'select',
    description: 'System color theme and appearance',
    options: ['lunia', 'classic', 'dark', 'light']
  },
  {
    id: 'CONF-008',
    category: 'appearance',
    setting: 'Language',
    value: 'en',
    type: 'select',
    description: 'System interface language',
    options: ['en', 'es']
  }
];

const mockSecurityLogs: SecurityLog[] = [
  {
    id: 'LOG-001',
    user: 'Dr. Sarah Johnson',
    action: 'login',
    timestamp: '2024-01-20 09:30:00',
    ipAddress: '192.168.1.100',
    userAgent: 'Chrome/120.0.0.0',
    status: 'success'
  },
  {
    id: 'LOG-002',
    user: 'Dr. Michael Brown',
    action: 'password_change',
    timestamp: '2024-01-20 08:45:00',
    ipAddress: '192.168.1.101',
    userAgent: 'Firefox/121.0.0',
    status: 'success'
  },
  {
    id: 'LOG-003',
    user: 'Unknown',
    action: 'login',
    timestamp: '2024-01-20 08:30:00',
    ipAddress: '203.45.67.89',
    userAgent: 'Unknown',
    status: 'failed'
  },
  {
    id: 'LOG-004',
    user: 'Jennifer Lee',
    action: 'data_access',
    timestamp: '2024-01-20 08:15:00',
    ipAddress: '192.168.1.102',
    userAgent: 'Safari/17.0.0',
    status: 'success'
  }
];

const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin': return 'bg-red-100 text-red-800 border-red-200';
    case 'pharmacist': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'technician': return 'bg-green-100 text-green-800 border-green-200';
    case 'manager': return 'bg-purple-100 text-purple-800 border-purple-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-800 border-green-200';
    case 'inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'suspended': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getLogStatusColor = (status: string) => {
  switch (status) {
    case 'success': return 'bg-green-100 text-green-800 border-green-200';
    case 'failed': return 'bg-red-100 text-red-800 border-red-200';
    case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getActionIcon = (action: string) => {
  switch (action) {
    case 'login': return <User className="w-4 h-4" />;
    case 'logout': return <User className="w-4 h-4" />;
    case 'password_change': return <Key className="w-4 h-4" />;
    case 'permission_change': return <Shield className="w-4 h-4" />;
    case 'data_access': return <Database className="w-4 h-4" />;
    default: return <User className="w-4 h-4" />;
  }
};

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [configValues, setConfigValues] = useState<Record<string, any>>({});

  // Initialize config values
  React.useEffect(() => {
    const initialValues: Record<string, any> = {};
    mockSystemConfigs.forEach(config => {
      initialValues[config.id] = config.value;
    });
    setConfigValues(initialValues);
  }, []);

  const handleConfigChange = (configId: string, value: any) => {
    setConfigValues(prev => ({
      ...prev,
      [configId]: value
    }));
  };

  const tabs = [
    { id: 'profile', label: 'User Profile', icon: User },
    { id: 'system', label: 'System Settings', icon: Settings },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'backup', label: 'Backup & Restore', icon: Database }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#4B0082] via-[#6B38FB] to-purple-600 rounded-3xl p-10 text-white shadow-2xl shadow-[#6B38FB]/25">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
              <Settings className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl font-black mb-2">System Settings</h1>
              <p className="text-xl text-purple-100">Configure system preferences, user profiles, and security settings</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Active Users</p>
              <p className="text-2xl font-black">24</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">System Status</p>
              <p className="text-2xl font-black text-green-300">Online</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Last Backup</p>
              <p className="text-2xl font-black">2h ago</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Security Score</p>
              <p className="text-2xl font-black">98/100</p>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Tabs */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 p-2 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                    isActive
                      ? 'bg-white text-[#6B38FB] shadow-lg'
                      : 'text-gray-600 hover:text-[#6B38FB] hover:bg-white/80'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="p-8">
          {/* User Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 bg-gradient-to-br from-[#4B0082] to-[#6B38FB] rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                  {mockUserProfile.avatar}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{mockUserProfile.name}</h3>
                  <p className="text-gray-600">{mockUserProfile.email}</p>
                  <div className="flex space-x-2 mt-2">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getRoleColor(mockUserProfile.role)}`}>
                      {mockUserProfile.role.charAt(0).toUpperCase() + mockUserProfile.role.slice(1)}
                    </span>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(mockUserProfile.status)}`}>
                      {mockUserProfile.status.charAt(0).toUpperCase() + mockUserProfile.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      defaultValue={mockUserProfile.name}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      defaultValue={mockUserProfile.email}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      defaultValue={mockUserProfile.phone}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                    <input
                      type="text"
                      defaultValue={mockUserProfile.department}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter current password"
                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <input
                      type="password"
                      placeholder="Enter new password"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-4">
                <button className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:border-gray-300 transition-all duration-300">
                  Cancel
                </button>
                <button className="px-6 py-3 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2">
                  <Save className="w-5 h-5" />
                  <span>Save Changes</span>
                </button>
              </div>
            </div>
          )}

          {/* System Settings Tab */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">System Configuration</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {mockSystemConfigs.map((config) => (
                  <div key={config.id} className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      {config.setting}
                    </label>
                    <p className="text-xs text-gray-500">{config.description}</p>
                    
                    {config.type === 'text' && (
                      <input
                        type="text"
                        value={configValues[config.id] || ''}
                        onChange={(e) => handleConfigChange(config.id, e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                      />
                    )}
                    
                    {config.type === 'boolean' && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={configValues[config.id] || false}
                          onChange={(e) => handleConfigChange(config.id, e.target.checked)}
                          className="w-5 h-5 text-[#6B38FB] border-gray-300 rounded focus:ring-[#6B38FB] focus:ring-2"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {configValues[config.id] ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    )}
                    
                    {config.type === 'number' && (
                      <input
                        type="number"
                        value={configValues[config.id] || ''}
                        onChange={(e) => handleConfigChange(config.id, parseInt(e.target.value))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                      />
                    )}
                    
                    {config.type === 'select' && config.options && (
                      <select
                        value={configValues[config.id] || ''}
                        onChange={(e) => handleConfigChange(config.id, e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
                      >
                        {config.options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end space-x-4 pt-6">
                <button className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:border-gray-300 transition-all duration-300 flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5" />
                  <span>Reset to Default</span>
                </button>
                <button className="px-6 py-3 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2">
                  <Save className="w-5 h-5" />
                  <span>Save Configuration</span>
                </button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Security Settings & Logs</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">Security Configuration</h4>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <p className="font-medium text-gray-900">Two-Factor Authentication</p>
                        <p className="text-sm text-gray-600">Require 2FA for all accounts</p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="w-5 h-5 text-[#6B38FB] border-gray-300 rounded focus:ring-[#6B38FB] focus:ring-2"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <p className="font-medium text-gray-900">Session Timeout</p>
                        <p className="text-sm text-gray-600">Auto-logout after inactivity</p>
                      </div>
                      <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-[#6B38FB]">
                        <option>15 minutes</option>
                        <option>30 minutes</option>
                        <option>1 hour</option>
                        <option>4 hours</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <p className="font-medium text-gray-900">Password Policy</p>
                        <p className="text-sm text-gray-600">Strong password requirements</p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          defaultChecked
                          className="w-5 h-5 text-[#6B38FB] border-gray-300 rounded focus:ring-[#6B38FB] focus:ring-2"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900">Recent Security Activity</h4>
                  
                  <div className="space-y-3">
                    {mockSecurityLogs.map((log) => (
                      <div key={log.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {getActionIcon(log.action)}
                            <span className="text-sm font-medium text-gray-900">{log.user}</span>
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getLogStatusColor(log.status)}`}>
                            {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <p>Action: {log.action.replace('_', ' ')}</p>
                          <p>Time: {log.timestamp}</p>
                          <p>IP: {log.ipAddress}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* User Management Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">User Management</h3>
                <button className="px-6 py-3 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2">
                  <Plus className="w-5 h-5" />
                  <span>Add New User</span>
                </button>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-gray-600 text-center">User management interface would be implemented here</p>
                <p className="text-gray-500 text-center text-sm mt-2">Features: Add, edit, delete users, manage roles and permissions</p>
              </div>
            </div>
          )}

          {/* Backup & Restore Tab */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Backup & Restore</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">System Backup</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Last Backup</span>
                      <span className="text-sm font-medium text-gray-900">2 hours ago</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Backup Size</span>
                      <span className="text-sm font-medium text-gray-900">2.4 GB</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                        Successful
                      </span>
                    </div>
                  </div>
                  <button className="w-full mt-4 px-4 py-3 bg-[#6B38FB] text-white rounded-xl font-semibold hover:bg-[#4B0082] transition-all duration-300">
                    Create Backup
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Restore Options</h4>
                  <div className="space-y-4">
                    <button className="w-full px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:border-[#6B38FB] transition-all duration-300 flex items-center justify-center space-x-2">
                      <Download className="w-5 h-5" />
                      <span>Download Backup</span>
                    </button>
                    <button className="w-full px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:border-[#6B38FB] transition-all duration-300 flex items-center justify-center space-x-2">
                      <Upload className="w-5 h-5" />
                      <span>Upload Backup</span>
                    </button>
                    <button className="w-full px-4 py-3 border-2 border-red-200 text-red-700 rounded-xl font-semibold hover:border-red-300 transition-all duration-300 flex items-center justify-center space-x-2">
                      <RefreshCw className="w-5 h-5" />
                      <span>Restore System</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
